import { useCallback, useEffect, useState } from 'react';

/**
 * Hash routing, in twenty lines, because that is what this app needs.
 *
 * ── Why not react-router ─────────────────────────────────────────────
 *
 * Six screens, no nested layouts, no loaders, no route-level code splitting, no
 * search-param state. A router earns its place when routes carry data
 * dependencies; here they carry a string.
 *
 * ── Why the HASH and not the path ────────────────────────────────────
 *
 * This is a static bundle on a static host. Path routing requires the host to
 * rewrite every unknown path to index.html, and when that rewrite is missing the
 * app works perfectly until somebody reloads on `/users` or pastes a link — then
 * they get the host's 404, which reads as "the admin portal is down". The hash
 * is never sent to the server, so a deep link cannot fail that way.
 *
 * The cost is uglier URLs. That is a fair trade for a surface whose links are
 * mostly pasted into incident channels, where "it 404s for me" is the last
 * thing anyone needs to debug.
 */

/**
 * `#/users/abc123` → `['users', 'abc123']`. Empty for the root.
 *
 * Exported and pure so it can be tested without a DOM — the parsing is the only
 * part of this file that can be wrong in a way worth catching, and a hash of
 * `''`, `'#'` and `'#/'` all have to mean the same thing or the Overview stops
 * being reachable from some of the ways a browser will hand it back.
 */
export function parseHash(hash: string): string[] {
  return stripQuery(hash).replace(/^#\/?/, '').split('/').filter(Boolean);
}

/**
 * `#/activity?user=abc` → `{ user: 'abc' }`.
 *
 * Cross-page navigation that CARRIES STATE is why this exists. "View activity"
 * on a profile has to open the Activity screen already scoped to that account,
 * and the alternative — lifting the selection into a shared parent — makes the
 * filter unlinkable. A hash query is a link, which is what gets pasted into an
 * incident channel.
 *
 * The query is stripped before segments are parsed, or `activity?user=abc`
 * becomes a screen name that matches nothing and the portal renders "no such
 * screen" for a link it generated itself.
 */
export function parseQuery(hash: string): Record<string, string> {
  const at = hash.indexOf('?');
  if (at === -1) return {};
  return Object.fromEntries(new URLSearchParams(hash.slice(at + 1)));
}

function stripQuery(hash: string): string {
  const at = hash.indexOf('?');
  return at === -1 ? hash : hash.slice(0, at);
}

const parse = () => parseHash(window.location.hash);
const parseQ = () => parseQuery(window.location.hash);

export function useRoute(): {
  segments: string[];
  query: Record<string, string>;
  navigate: (to: string) => void;
} {
  const [segments, setSegments] = useState<string[]>(parse);
  const [query, setQuery] = useState<Record<string, string>>(parseQ);

  useEffect(() => {
    const onChange = () => {
      setSegments(parse());
      setQuery(parseQ());
    };
    window.addEventListener('hashchange', onChange);
    // Read once more on mount: a hash set between render and this effect
    // running would otherwise be missed, and the app would show the wrong
    // screen until the next navigation.
    onChange();
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  const navigate = useCallback((to: string) => {
    window.location.hash = to.startsWith('/') ? to : `/${to}`;
  }, []);

  return { segments, query, navigate };
}
